/**
 * The composed application.
 *
 * A host that wants the whole thing renders this. A host that wants only the
 * conversation renders `<Conversation>` inside `<Chat42Provider>` and passes
 * `CONVERSATION_ONLY` surfaces — which is B8a.5's "which panels exist", and is
 * why the layout below is computed from surfaces rather than hardcoded.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Chat42Provider, Fleet, Conversation, INSTALLED_SETTINGS, isUnread, type Chat42ProviderProps,
} from './components.tsx';
import { NewSession, useNewSessionKey } from './newsession.tsx';
import {
  Artifacts, Connections, ContextPanel, Credits, Devices, hasContextFor, Inbox, Memory,
  PlatformAiServices, PlatformGitHosts, PlatformCredits, PlatformPolicies, Profile, Repos, Schedules, Settings, Skills, Work,
} from './panels.tsx';
import { settingsFromAddress } from './host-folder.ts';
import { shallowEqual, useChat42, useChat42Select, useChat42State } from './context.ts';
import { isAppSession } from './session-list.ts';
import { themeStyle, type Surfaces } from './theme.ts';
import type {
  BuiltinSettingsSection, SettingsContribution, SettingsRoom, SettingsSection, SettingsTab,
} from './settings-seam.ts';
import { BackgroundTasksPanel } from './tasks.tsx';
import { AuditRoom } from './audit-room.tsx';
import { countGroups, summaryText } from './tasks-format.ts';
import { AppFailure, Boundary, Broken, FailureSessionCtx } from './boundary.tsx';

/**
 * `draft` is a session that does not exist yet.
 *
 * Its own view rather than a flag on `session`, because the difference is not
 * cosmetic: there is no id, so nothing that keys off one — the context panel,
 * the mark-read effect, the dock — should run at all.
 */
type View = 'draft' | 'session' | 'inbox' | 'work' | 'schedules' | 'artifacts';

/*
 * The seam's own types now live in `settings-seam.ts`, and are re-exported here
 * so nothing that imported them from this file has to move. They had to leave:
 * the provider takes the installed plugin list, `Chat42ProviderProps` is in
 * `components.tsx`, and this file imports that one — so a type declared here
 * could not be named there without a cycle. That cycle is the whole reason a
 * host could not supply a plugin list at all.
 */
export type {
  BuiltinSettingsSection, SettingsContribution, SettingsRoom, SettingsSection, SettingsTab,
} from './settings-seam.ts';

/** Rooms behind the platform door. Only a platform administrator sees any. */
export type PlatformSection = 'credits' | 'policies' | 'git-hosts' | 'ai-services';

/**
 * The plugins whose settings rooms this build carries, when the host names none.
 *
 * A default rather than the only answer now: `Chat42ProviderProps.settings`
 * supplies the list, and this is what a build that supplies none gets — which
 * is every build today, so nothing changes for anybody until a host says
 * otherwise. The constant itself lives beside the provider that defaults to it,
 * because this file imports that one and the reverse would be a cycle.
 */
const PLUGIN_SETTINGS: readonly SettingsContribution[] = INSTALLED_SETTINGS;

/**
 * The rooms behind the settings door that this host has not suppressed, in order.
 *
 * One list for the dialog's side menu and for where the account menu's
 * "Settings" lands, so the two cannot disagree about which rooms exist — a door
 * that opened onto a room the side menu did not list would be a blank page.
 */
export function settingsRooms(
  surfaces: Surfaces,
  plugins: readonly SettingsContribution[] = PLUGIN_SETTINGS,
): SettingsRoom[] {
  const rooms: Array<[BuiltinSettingsSection, string, boolean] | [BuiltinSettingsSection, string, boolean, readonly SettingsTab[]]> = [
    // First, because it is the one room that is about the person rather than
    // about the product.
    ['profile', 'Profile', true],
    ['general', 'General', surfaces.settings],
    ['tools', 'Tools', surfaces.tools],
    ['repos', 'Repositories', surfaces.repos],
    ['machines', 'Machines', surfaces.devices],
    // Two libraries: a person's own, and their organisation's where it has one.
    //
    // The only built-in room with divisions, and it used to draw its own pair
    // of buttons inside the body and remember them in `localStorage` — while
    // every other room's tab state was held above the dialog so it survived a
    // close. Two patterns for one thing in one dialog. This is the seam the
    // plugin rooms already use, and the room reads its tab from it.
    ['skills', 'Skills & policies', surfaces.skills, [
      { id: 'personal', label: 'Personal' },
      { id: 'organisation', label: 'Organisation' },
    ]],
    ['memory', 'Memory', surfaces.memory],
    ['credits', 'Credits', surfaces.credits],
    // Last of the product's own: read rarely, and about the organisation rather
    // than about one person's setup. Gated on Settings and nothing narrower,
    // because the route it reads answers everybody who can open Settings — a
    // room hidden from somebody the server would serve is the client deciding
    // who may see the trail. See audit-room.tsx.
    ['audit', 'Audit trail', surfaces.settings],
  ];
  const built: SettingsRoom[] = rooms
    .filter(([, , on]) => on)
    .map(([id, label, , tabs]) => ({ id, label, ...(tabs?.length ? { tabs } : {}) }));

  // Last, and gated on the same surface as the rest of the product's own
  // settings: a host that suppressed Settings because it configures Chat42
  // itself does not want a plugin opening a second way in.
  if (!surfaces.settings) return built;
  return [
    ...built,
    ...plugins.map((p) => ({
      id: p.id,
      label: p.label,
      ...(p.tabs?.length ? { tabs: p.tabs } : {}),
    })),
  ];
}

/**
 * Which sub-tab a room is showing.
 *
 * Falls back to the first rather than to nothing, including when what was
 * remembered no longer exists — a plugin that drops a tab between releases
 * would otherwise leave somebody on a room with an empty body and no way to
 * tell why.
 */
export function activeTab(
  room: SettingsRoom | undefined,
  remembered: Readonly<Record<string, string>>,
): string {
  const tabs = room?.tabs;
  if (!room || !tabs?.length) return '';
  const was = remembered[room.id];
  return was && tabs.some((t) => t.id === was) ? was : tabs[0]!.id;
}

export function Chat42Workspace() {
  const { surfaces, theme, client, settings: installed, hostSessionId, onSessionChanged } = useChat42();
  // A host that wants to be told which session is shown is a host that decides it. Taken from
  // the callback rather than a flag of its own: the two would be set together every time, and a
  // flag that can disagree with the thing it describes is one that eventually does.
  const hostOwnsSession = Boolean(onSessionChanged);
  const [view, setView] = useState<View>('session');
  const [railOpen, setRailOpen] = useRailOpen();
  const [settingsAt, setSettingsAt] = useState<SettingsSection | null>(null);
  /**
   * Where somebody was inside each room, by room id.
   *
   * Held above the dialog rather than in it, so closing Settings and opening it
   * again lands where you were. A room's divisions are where the work is — a
   * person checking one autonomy limit twice in a row should not have to walk
   * back through the room both times.
   */
  const [settingsTab, setSettingsTab] = useState<Record<string, string>>({});
  const [platformAt, setPlatformAt] = useState<PlatformSection | null>(null);
  const [currentId, setCurrentId] = useState<string | undefined>();
  // What the shell draws from, and no more: an event in a conversation changes none of this,
  // so the shell, the list and the conversation are not drawn again for it (useChat42Select).
  const state = useChat42Select((all) => ({
    sessions: all.sessions,
    tasksByRoot: all.tasksByRoot,
    schedules: all.schedules,
    permissionError: all.permissionError,
    platformAdmin: all.platformAdmin,
    connected: all.connected,
    // Decided here rather than handed the journals, which change with every event anywhere.
    withContext: hasContextFor(all, surfaces, currentId),
    // Where to land with nothing chosen: the first session the list shows, so not an app's
    // while those are hidden (session-list.ts).
    firstShown: all.sessions.find((s) => all.settings?.effective.appSessions === 'shown' || !isAppSession(s) || s.pinned)?.id,
  }), shallowEqual);
  /**
   * The background tasks panel: one conversation's tasks, or everyone's.
   *
   * Held here rather than by the controls that open it, so the header's
   * "N running" and the rail's entry open the same panel — two of them stacked
   * at the right edge would be two lists of the same work.
   */
  const [tasksAt, setTasksAt] = useState<{ root?: string } | null>(null);

  /**
   * Somewhere else asking for a settings screen.
   *
   * An event rather than a prop: the provider sits above this component, so
   * anything rendered inside it — the "where should this run" sheet, for one —
   * cannot be handed a callback that reaches this state. One listener here is
   * less machinery than threading a setter through everything between.
   */
  useEffect(() => {
    const open = (e: Event) => {
      const at = (e as CustomEvent<SettingsSection>).detail;
      if (at) setSettingsAt(at);
    };
    window.addEventListener('chat42:settings', open);
    // Named in the address: where a framed picker's "Pair this computer" sends somebody, in a
    // tab of its own (ADR 0106). Read once and taken out, so a reload does not open it again.
    const named = typeof window.location?.search === 'string' ? settingsFromAddress(window.location.search) : undefined;
    if (named) {
      setSettingsAt(named);
      const url = new URL(window.location.href);
      url.searchParams.delete('settings');
      window.history.replaceState(window.history.state, '', url.toString());
    }
    return () => window.removeEventListener('chat42:settings', open);
  }, []);

  // Land on whatever most needs a person, which is what the fleet ordering
  // already put first — rather than on whatever happens to be newest.
  useEffect(() => {
    // Not while drafting. An empty `currentId` is the whole point of that
    // screen, and this would snap it to whatever is at the top of the fleet
    // the moment somebody clicked New.
    if (view === 'draft') return;
    // Not when a host chooses. Landing somewhere sensible is right for a person who opened
    // Chat42 to work; a host opened it to talk about one thing, and the fleet's first session
    // is simply whatever else was waiting. Worse than showing nothing: the frame reports what
    // it is showing, so a host that stores the id records this unrelated conversation as the
    // one belonging to its record, and reopens it from then on.
    if (hostOwnsSession) return;
    if (!currentId && state.firstShown) setCurrentId(state.firstShown);
  }, [currentId, state.firstShown, view, hostOwnsSession]);

  /**
   * A session the host asked for wins over whichever this picked.
   *
   * Only when it changes, and only onto the session view: a host that names a session is
   * choosing which conversation this frame is about, but a person who then walks somewhere else
   * inside the frame should stay there rather than being pulled back on the next render.
   */
  useEffect(() => {
    if (!hostSessionId || hostSessionId === currentId) return;
    setCurrentId(hostSessionId);
    setView('session');
    // currentId is deliberately not a dependency: this follows the host's requests, not the
    // person's movements, and reacting to both is what pulls somebody back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostSessionId]);

  /**
   * Tell the host which session is shown.
   *
   * A host embedding this has to persist the id to reopen the same conversation, and the id of
   * a session created inside the frame is knowable no other way. Title and state travel with it
   * so the host can label its own button without asking.
   */
  useEffect(() => {
    if (!onSessionChanged || !currentId) return;
    const session = state.sessions.find((candidate) => candidate.id === currentId);
    if (!session) return;
    onSessionChanged({ id: session.id, title: session.title ?? '', state: String(session.state ?? '') });
  }, [onSessionChanged, currentId, state.sessions]);

  const open = (id: string) => {
    setCurrentId(id);
    setView('session');
  };

  /** A blank screen that commits nothing. See newsession.tsx. */
  const startNew = useCallback(() => {
    setCurrentId(undefined);
    setView('draft');
  }, []);
  useNewSessionKey(startNew);

  /**
   * Reading a session is what marks it read.
   *
   * An effect on *what is on screen*, not a line in the click handler, because
   * clicking is only one of three ways a session ends up in front of somebody
   * and the other two were both missing it: landing on the first session at
   * startup never went through `open`, and a reply arriving while you are
   * already looking at the session does not go through it either — so the mark
   * stayed lit on the conversation being read, which is precisely when it
   * should not.
   *
   * Never a button. Anything you have to tidy up by hand stops being a signal
   * and becomes housekeeping, and housekeeping is what people stop doing.
   */
  // Every task this person has, for the rail's entry. Its words and its colour
  // come from the same summary the panel shows, so the two cannot disagree.
  const allTasks = Object.values(state.tasksByRoot).flat();
  const taskCounts = countGroups(allTasks);

  const showing = view === 'session' ? state.sessions.find((s) => s.id === currentId) : undefined;
  const unseen = !!showing && isUnread(showing);
  useEffect(() => {
    if (!unseen || !currentId) return;
    void client.markSeen(currentId).catch(() => {});
  }, [unseen, currentId, client]);

  /**
   * The side columns as *variables*, not as a grid template.
   *
   * `gridTemplateColumns` was set inline, and an inline style is the one thing a
   * media query cannot override without `!important` — so on a phone the app
   * kept trying to lay out a 260px rail, a 260px context panel and a
   * conversation in 375 points, and none of the three was usable. Setting the
   * widths as custom properties leaves the template itself in the stylesheet,
   * where a narrow screen can replace it outright.
   */
  const sideWidths = {
    // The full width comes from the stylesheet so the rail's own `width` and the
    // grid track it sits in cannot drift apart — they have to match exactly or
    // the slide reflows the list as it moves.
    '--c42-rail-w': surfaces.rail && railOpen ? 'var(--c42-rail-full)' : '0px',
    // Only when the panel would actually render something. It returns null
    // without a work item and a pinned directive — which is most sessions — so
    // reserving the column on the strength of the surface being *enabled* left
    // a 260px band of nothing down the right of every ordinary conversation.
    '--c42-ctx-w': view === 'session' && state.withContext
      ? '260px' : '0px',
  } as React.CSSProperties;

  // Where the account menu's "Settings" lands: the first room that is about the
  // product rather than the person. None at all, and the entry is not drawn.
  //
  // The installed list, not the default. This computed from `settingsRooms
  // (surfaces)` with no plugins while the dialog below computed from one, and
  // the two agreed only because both fell back to the same constant — so the
  // first host to supply a list would have had the menu landing somewhere the
  // dialog did not draw.
  const settingsRoom = settingsRooms(surfaces, installed).find((r) => r.id !== 'profile')?.id;

  /**
   * A refusal about what this account may do.
   *
   * Shown instead of the app rather than beside it: nothing works without
   * `chat42.access`, so a half-rendered interface throwing the same 403 at
   * every panel would be worse than one screen saying what is missing. It names
   * the permission and the role, because the point of enforcing these was to be
   * able to find and fix a wrong grant.
   */
  if (state.permissionError) {
    const p = state.permissionError;
    return (
      <div className="c42" style={{ ...themeStyle(theme), ...sideWidths }}>
        {/* Sign-out and nothing else. Somebody signed in with the wrong account
            is exactly who needs it, and a screen with no way out except
            clearing site data is how they would otherwise have to leave. */}
        {client.canSignOut && <AccountMenu />}
        <main className="c42-main">
          <div className="c42-scroll"><div className="c42-wrap">
            {/* One statement, then the detail behind a disclosure.
                This screen used to print the server's message — which already names the missing
                permission, the role and every permission the account holds — and then a second
                paragraph saying all three again. Forty permission strings, twice, is not a
                better explanation than one sentence; it is the same sentence buried. */}
            <div className="c42-hero">
              <div className="c42-heromark">42</div>
              <h2>This account cannot use Chat42 yet</h2>
              <p className="c42-why">
                It needs <code className="mono">{p.permission}</code>, which comes with the{' '}
                <code className="mono">{p.role}</code> role. An administrator grants that at
                auth.rule42.ai.
              </p>
              <p className="c42-why">
                {p.held.length
                  ? 'Everything else about this account is in order — it is signed in and holds its other permissions.'
                  : 'This account holds no Chat42 permissions at all, which usually means the role was never granted.'}
              </p>
              {p.held.length > 0 && (
                <details className="c42-held">
                  <summary>What this account does hold ({p.held.length})</summary>
                  <ul>
                    {[...p.held].sort().map((permission) => (
                      <li key={permission}><code className="mono">{permission}</code></li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div></div>
        </main>
      </div>
    );
  }

  // One control, drawn in one of three places — see `AccountPlacement`.
  const account = (placement: AccountPlacement) => (
    <AccountMenu
      placement={placement}
      onProfile={() => setSettingsAt('profile')}
      {...(settingsRoom ? { onSettings: () => setSettingsAt(settingsRoom) } : {})}
      // Only for somebody who can use it. Not greyed out, not present: an
      // entry that exists and refuses is an invitation to wonder what is
      // behind it.
      {...(state.platformAdmin ? { onPlatform: () => setPlatformAt('credits') } : {})}
    />
  );
  // With no rail to carry it, the chip goes in the conversation header's own
  // row, at the end of its doors, whenever there is a conversation on screen.
  // It used to float over the shell's top right instead, and that corner is
  // where a docked panel's close is: the chip sat on top of it, and on the
  // header's ⋯ when nothing was docked (2026-10-18). In the header it is laid
  // out with the doors, so a panel opening pushes it left with them and
  // nothing can be underneath it. Floating is kept only where no conversation
  // is shown, which has no panels to cover.
  const inHead = !surfaces.rail && surfaces.account && Boolean(showing);
  // Where the account menu is not drawn at all, Settings still needs a way in,
  // or a host that keeps `settings` would have rooms nobody can open. The
  // header's ⋯ carries it: an existing menu, rather than a new control
  // floating over the work.
  const settingsInPanels = !surfaces.account && settingsRoom
    ? () => setSettingsAt(settingsRoom)
    : undefined;

  return (
    <div
      className="c42"
      // Read by the stylesheet rather than by JavaScript: the drawer, the
      // backdrop and the toggle all key off this one attribute.
      data-rail={surfaces.rail ? (railOpen ? 'open' : 'closed') : 'none'}
      style={{ ...themeStyle(theme), ...sideWidths }}
    >
      {surfaces.rail && !railOpen && (
        /* The way back. Only when the rail is hidden, so it never sits on top
           of the thing it opens. */
        <button
          className="c42-railshow"
          aria-label="Show sessions"
          onClick={() => setRailOpen(true)}
        >☰</button>
      )}

      {surfaces.rail && railOpen && (
        /* Tapping away closes the drawer. Present only on narrow screens, where
           the rail floats over the conversation — on a wide one it is a column
           and there is nothing to dismiss. */
        <button
          className="c42-railback"
          aria-label="Close sessions"
          onClick={() => setRailOpen(false)}
        />
      )}

      {surfaces.rail && (
        /* Mounted whether or not it is showing, and slid out of the way by the
           stylesheet. Unmounting it collapsed the conversation as well: the
           columns are auto-placed, so with the rail gone the conversation fell
           into the rail's track — which is 0px wide when the rail is hidden.
           That is the whole of "hiding the list also hides the session". Being
           mounted is also what gives it something to animate from. */
        // The rail's own fallback keeps the rail's column, because the columns are
        // placed by the stylesheet and a fallback with no class would fall into
        // the conversation's.
        <Boundary
          area="rail" resetKeys={[state.sessions.length]}
          fallback={({ error, reset }) => (
            <aside className="c42-rail"><Broken what="The session list" error={error} onRetry={reset} /></aside>
          )}
        >
        <Fleet
          currentId={currentId}
          onSelect={open}
          head={
            <NavTop
              view={view} setView={setView}
              schedules={state.schedules.filter((s) => s.enabled).length}
              showInbox={surfaces.inbox} showSchedules={surfaces.schedules}
              showWork={surfaces.work}
              showArtifacts={surfaces.artifacts}
              tasks={allTasks.length ? summaryText(allTasks) : undefined}
              tasksNeedYou={taskCounts.needs}
              tasksRunning={taskCounts.working}
              tasksOpen={tasksAt !== null && !tasksAt.root}
              // A second press closes it, and pressing it over one
              // conversation's tasks widens the panel to everyone's.
              onTasks={() => setTasksAt((was) => (was && !was.root ? null : {}))}
              onNew={startNew}
              drafting={view === 'draft'}
            />
          }
          // Who is signed in, at the bottom of the list. The reconnect notice
          // joins it above rather than replacing it — it is about the
          // connection, not about the account.
          foot={
            <>
              {!state.connected && <NavFoot onReconnect={() => client.connect()} />}
              {surfaces.account && account('rail')}
            </>
          }
          onHide={() => setRailOpen(false)}
        />
        </Boundary>
      )}

      {/* Only where there is no rail to carry it and no conversation header
          to sit in: the empty screen, or a new session's draft. Neither has a
          panel docked, so there is nothing at the top right to cover. With a
          rail it sits at the rail's foot, and with a conversation in its
          header (`inHead`), and this floating copy would be a second one. */}
      {!surfaces.rail && surfaces.account && !inHead && account('float')}

      {platformAt && (
        <Boundary
          area="platform" part={platformAt}
          fallback={({ error, reset }) => (
            <DialogFailure label="Platform" error={error} onRetry={reset} onClose={() => setPlatformAt(null)} />
          )}
        >
        <PlatformModal section={platformAt} onSection={setPlatformAt}
          onClose={() => setPlatformAt(null)} />
        </Boundary>
      )}

      {/* The middle column, whichever view it is, behind one boundary: a
          conversation that cannot be drawn leaves the rail working, so somebody
          can go to another one. Tried again on a move to another view or
          session, and not keyed by them, because a key would remount the
          conversation on every switch, which it is written to avoid (it would
          refetch the journal and lose the scroll). */}
      <Boundary
        area={view === 'session' ? 'conversation' : view} resetKeys={[view, currentId]}
        fallback={({ error, reset }) => (
          <main className="c42-main"><div className="c42-wrap">
            <Broken what={view === 'session' ? 'This conversation' : 'This screen'} error={error} onRetry={reset} />
          </div></main>
        )}
      >
      {view === 'draft' && <NewSession onCreated={open} />}
      {view === 'artifacts' && <main className="c42-main"><Artifacts /></main>}
      {view === 'session' && (
        <Conversation
          sessionId={currentId}
          onOpenTasks={(root) => setTasksAt({ root })}
          onOpenSession={open}
          {...(inHead ? { account: account('head') } : {})}
          {...(settingsInPanels ? { onSettings: settingsInPanels } : {})}
        />
      )}
      {view === 'inbox' && <main className="c42-main"><Inbox onOpen={open} /></main>}
      {view === 'schedules' && <main className="c42-main"><Schedules /></main>}
      {/* One screen. The tools a service offers live inside that service,
          because "what can this reach?" is a question about the service. */}
      {view === 'work' && <main className="c42-main"><Work onOpen={open} /></main>}
      </Boundary>

      {view === 'session' && (
        <Boundary
          area="context" resetKeys={[currentId]}
          fallback={({ error, reset }) => (
            <div className="c42-ctx"><Broken what="This panel" error={error} onRetry={reset} /></div>
          )}
        >
          <ContextPanel sessionId={currentId} />
        </Boundary>
      )}

      {tasksAt && (
        <Boundary
          key={tasksAt.root ?? 'everyone'} area="tasks"
          // Fixed over the page with its own close, so the fallback keeps both:
          // a panel that cannot be drawn must still be one somebody can close.
          fallback={({ error, reset }) => (
            <section className="c42-tasks" aria-label="Background tasks">
              <div className="c42-taskshd">
                <span className="c42-spacer" />
                <button type="button" className="c42-icon" aria-label="Close" onClick={() => setTasksAt(null)}>✕</button>
              </div>
              <Broken what="This panel" error={error} onRetry={reset} />
            </section>
          )}
        >
        <BackgroundTasksPanel
          // Remounted when it changes scope, so it loads the list it now shows.
          key={tasksAt.root ?? 'everyone'}
          {...(tasksAt.root ? { rootSessionId: tasksAt.root } : {})}
          onClose={() => setTasksAt(null)}
          onOpenSession={open}
        />
        </Boundary>
      )}

      {settingsAt && (
        <Boundary
          area="settings" part={settingsAt}
          fallback={({ error, reset }) => (
            <DialogFailure label="Settings" error={error} onRetry={reset} onClose={() => setSettingsAt(null)} />
          )}
        >
        <SettingsModal
          section={settingsAt}
          onSection={setSettingsAt}
          onClose={() => setSettingsAt(null)}
          surfaces={surfaces}
          tabs={settingsTab}
          onTab={(room, tab) => setSettingsTab((was) => ({ ...was, [room]: tab }))}
        />
        </Boundary>
      )}
    </div>
  );
}

/**
 * A dialog that could not be drawn: the fallback in the dialog's own frame, with its close.
 * Without the close the backdrop would stay over the app with nothing on it to dismiss.
 */
function DialogFailure(
  { label, error, onRetry, onClose }:
  { label: string; error: Error; onRetry: () => void; onClose: () => void },
) {
  return (
    <div className="c42-modal-back centred" onClick={onClose}>
      <div className="c42-settings c42-dialogbroken" role="dialog" aria-modal="true" aria-label={label}
        onClick={(e) => e.stopPropagation()}>
        <Broken what={label} error={error} onRetry={onRetry} />
        <div className="c42-acts"><button type="button" className="c42-mini" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

/**
 * Where the account control is drawn.
 *
 * - `rail`: at the foot of the session list. That is where it sits in every app
 *   that has a rail, and it is out of the way of the work.
 * - `head`: at the end of the conversation header's doors, in their row, for a
 *   shell with no rail. Laid out rather than floating, so it can never be on
 *   top of anything: a panel opening beside the conversation pushes it left
 *   with the doors.
 * - `float`: over the shell's top right. Only where nothing is docked — the
 *   empty screen, a draft, the refusal screen — because that corner belongs to
 *   the panels: it is where a docked panel's own controls are, and a chip
 *   floating there covered the close button of whatever was open. It did that
 *   twice: once beside the rail (moved to the foot, 2026-09-15), and again in
 *   rule42-cloud's framed panel, which has no rail (2026-10-18).
 */
type AccountPlacement = 'rail' | 'head' | 'float';

/**
 * The person: who is signed in, and the doors that are about them rather than
 * about the work.
 *
 * It began at the top right, because that is where web applications keep the
 * account and a sign-out somebody has to hunt for is one they do not find.
 * Where it is drawn now depends on the shell (`AccountPlacement`), because that
 * corner turned out to belong to the panels. Whether it is drawn at all is the
 * host's `account` surface: a host that names the person itself turns it off.
 *
 * Every entry is optional and drawn only when it can do something. A host
 * that owns sign-in gives the client no `signOut`, and the menu offers none —
 * signing somebody out of Chat42 alone would leave them signed in to the
 * product around it.
 */
function AccountMenu(props: {
  onProfile?: () => void;
  onSettings?: () => void;
  onPlatform?: () => void;
  /** See `AccountPlacement`. Floating unless told otherwise. */
  placement?: AccountPlacement;
}) {
  const { client } = useChat42();
  const { me } = useChat42State();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const who = me?.name ?? me?.email;
  const initials = initialsOf(who);
  // The organisation where the issuer names it; otherwise say it is one
  // person's account, which is the other thing the Profile room distinguishes.
  const org = me?.orgName ?? (me?.personal ? 'Individual account' : undefined);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    // Into the menu on opening, so the arrow keys work straight away — the
    // menu-button pattern screen readers expect.
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    const outside = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Stopped here, so an Escape meant for the menu does not also reach
      // whatever else on the page listens for one.
      e.stopPropagation();
      close(true);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open, close]);

  const arrows = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'));
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') setOpen(false);
  };

  const choose = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  const signOut = () => {
    // Said before anything is awaited. Revoking the token is a round trip to
    // the issuer, and a menu that sits there looking unclicked while it
    // happens gets clicked again.
    setLeaving(true);
    void client.signOut().catch(() => setLeaving(false));
  };

  const places = Boolean(props.onProfile || props.onSettings || props.onPlatform);

  return (
    <div
      className={`c42-account${props.placement === 'rail' ? ' inrail' : props.placement === 'head' ? ' inhead' : ''}`}
      ref={root}
    >
      <button
        ref={button}
        className="c42-accountbtn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={who ? `Account: ${who}` : 'Account'}
        title={who}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="c42-avatar" aria-hidden="true">{initials}</span>
        <span className="c42-accountname">{who ?? 'Account'}</span>
        <span className="c42-accountcaret" aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div className="c42-menu c42-accountmenu">
          {/* Outside the menu role: a menu holds items, and a name read out as
              an item nobody can choose is noise to a screen reader. */}
          <div className="c42-accounthd">
            <span className="c42-avatar lg" aria-hidden="true">{initials}</span>
            <span className="c42-accountwho">
              <span className="c42-accountfull">{me?.name ?? me?.email ?? 'Signed in'}</span>
              {me?.name && me.email && <span className="c42-why">{me.email}</span>}
              {org && <span className="c42-why">{org}</span>}
            </span>
          </div>

          <div id={menuId} role="menu" aria-label="Account" onKeyDown={arrows}>
            {props.onProfile && (
              <button role="menuitem" onClick={choose(props.onProfile)}>Profile</button>
            )}
            {props.onSettings && (
              <button role="menuitem" onClick={choose(props.onSettings)}>Settings</button>
            )}
            {props.onPlatform && (
              <button role="menuitem" onClick={choose(props.onPlatform)}>Platform settings</button>
            )}
            {client.canSignOut && (
              <>
                {places && <div className="c42-menusep" role="separator" />}
                <button role="menuitem" disabled={leaving} onClick={signOut}>
                  {leaving ? 'Signing out…' : 'Sign out'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Up to two letters: the first and last names' initials, or the first letter
 * of an address. A question mark until somebody is known, rather than a blank
 * circle that looks like a rendering fault.
 */
function initialsOf(who: string | undefined): string {
  const base = (who ?? '').trim();
  const words = (base.includes('@') && !base.includes(' ') ? base.split('@')[0]! : base)
    .split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0]!.charAt(0);
  const last = words.length > 1 && !base.includes('@') ? words[words.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}

/**
 * Settings, as one door with rooms behind it.
 *
 * Everything here is set up once and changed rarely, which is why it was wrong
 * as four rail entries: four things permanently competing for attention with
 * the work itself. A modal is the right shape for something you open, change,
 * and close.
 *
 * The side menu rather than tabs because the list will grow — the PRD has
 * connector authoring, plugins, policy and audit still to come, and tabs stop
 * working at about six.
 */
/**
 * The dialog's own controls: expand, and close.
 *
 * Shared by both rooms rather than written twice. They are the same dialog in
 * two moods, and a full-screen button that existed in only one of them would be
 * the kind of difference nobody can explain.
 */
function DialogTools(
  { wide, onWide, onClose }: { wide: boolean; onWide: () => void; onClose: () => void },
) {
  return (
    <span className="c42-settings-tools">
      <button className="c42-icon" aria-pressed={wide}
        title={wide ? 'Exit full screen' : 'Full screen'}
        aria-label={wide ? 'Exit full screen' : 'Full screen'}
        onClick={onWide}>{wide ? '⤡' : '⤢'}</button>
      <button className="c42-icon" onClick={onClose} aria-label="Close">✕</button>
    </span>
  );
}

/**
 * A room's divisions, in a column of their own beside the main list.
 *
 * Buttons with `aria-current`, matching the main nav rather than a `tablist`,
 * because that is what it is: a second level of the same navigation, in the
 * same shape, so somebody who has learned one column has learned both.
 *
 * Up and down move between them, which a plain row of buttons does not do on
 * its own. Switching is local state and paints immediately — nothing here waits
 * on a request, which is the 100ms rule holding for navigation whatever the
 * room behind it is doing.
 */
function SubNav(
  { room, tabs, at, onTab }: {
    room: SettingsRoom;
    tabs: readonly SettingsTab[];
    at: string;
    onTab: (id: string) => void;
  },
) {
  const move = (e: React.KeyboardEvent<HTMLElement>, from: number) => {
    const to = e.key === 'ArrowDown' ? from + 1
      : e.key === 'ArrowUp' ? from - 1
        : e.key === 'Home' ? 0
          : e.key === 'End' ? tabs.length - 1
            : undefined;
    if (to === undefined) return;
    e.preventDefault();
    const next = tabs[(to + tabs.length) % tabs.length];
    if (!next) return;
    onTab(next.id);
    // Focus follows the selection, or the next arrow press moves from where the
    // focus was left rather than from where the person now is.
    const buttons = e.currentTarget.parentElement?.querySelectorAll('button');
    (buttons?.[(to + tabs.length) % tabs.length] as HTMLElement | undefined)?.focus();
  };

  return (
    <nav className="c42-settings-sub" aria-label={`${room.label} sections`}>
      <div className="c42-settings-title">{room.label}</div>
      {tabs.map((t, i) => (
        <button key={t.id} className="c42-nav" aria-current={t.id === at}
          onKeyDown={(e) => move(e, i)}
          onClick={() => onTab(t.id)}>
          <span>{t.label}</span>
        </button>
      ))}
    </nav>
  );
}

export function SettingsModal(props: {
  section: SettingsSection;
  onSection: (s: SettingsSection) => void;
  onClose: () => void;
  surfaces: Surfaces;
  /** Where somebody was in each room, by room id. */
  tabs?: Readonly<Record<string, string>>;
  onTab?: (room: string, tab: string) => void;
  /**
   * The rooms to draw, overriding what the host installed.
   *
   * A test seam, and now only a test seam: the host's own list arrives through
   * the provider, so the account menu and this dialog read the same one. It
   * stays because a test that has to stand up a provider to check a plugin room
   * is a test nobody writes.
   */
  plugins?: readonly SettingsContribution[];
}) {
  const { settings: installed } = useChat42();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  const plugins = props.plugins ?? installed ?? PLUGIN_SETTINGS;
  const shown = settingsRooms(props.surfaces, plugins);
  const room = shown.find((r) => r.id === props.section);
  const plugin = plugins.find((p) => p.id === props.section);
  const tab = activeTab(room, props.tabs ?? {});
  /**
   * A `plugin:` id nobody installed.
   *
   * `setSettingsAt` takes any `SettingsSection`, including one arriving on the
   * `chat42:settings` window event, so a host or a stale link can name a plugin
   * that is not here. Every `props.section === …` arm missed, `plugin` was
   * undefined, and the body rendered `null` — the dialog opened with the room
   * list drawn, nothing highlighted, and a completely empty page. That is the
   * failure this room's own six states exist to prevent, one level above where
   * they were implemented.
   *
   * The panel seam already states the rule — "a saved dock naming an
   * uninstalled plugin must degrade to a dock without it" — and this is the
   * same rule for the same reason.
   */
  const missingPlugin = props.section.startsWith('plugin:') && !plugin
    ? props.section.slice('plugin:'.length)
    : undefined;
  /**
   * Open expanded.
   *
   * These rooms outgrew the 880px column they were designed around — a month
   * of usage, a list of every account, a card of settings each. Opening small
   * and making somebody widen it every time is asking them to do the same
   * thing on every visit.
   */
  const [wide, setWide] = useState(true);

  return (
    <div className="c42-modal-back centred" onClick={props.onClose}>
      <div className={`c42-settings${wide ? ' full' : ''}`}
        role="dialog" aria-modal="true" aria-label="Settings"
        onClick={(e) => e.stopPropagation()}>
        <nav className="c42-settings-nav">
          {/* The door and the room behind it share a name, so clicking
              "Settings" and landing on something titled otherwise does not read
              as having gone somewhere else. */}
          <div className="c42-settings-title">Settings</div>
          {shown.map((r) => (
            <button key={r.id} className="c42-nav" aria-current={props.section === r.id}
              onClick={() => props.onSection(r.id)}>
              <span>{r.label}</span>
            </button>
          ))}
        </nav>

        {/*
          The second column, and only when a room has one.

          A room with no divisions draws exactly as it always did — one nav, one
          body — because giving every room an empty second column to be
          consistent would put a permanent 170px of nothing beside eight screens
          that never needed it.
        */}
        {room?.tabs?.length ? (
          <SubNav
            room={room} tabs={room.tabs} at={tab}
            onTab={(id) => props.onTab?.(room.id, id)}
          />
        ) : null}

        <div className="c42-settings-body">
          <DialogTools
            wide={wide} onWide={() => setWide((was) => !was)} onClose={props.onClose}
          />
          {props.section === 'profile' && <Profile />}
          {props.section === 'general' && <Settings />}
          {props.section === 'tools' && <Connections />}
          {props.section === 'repos' && <Repos />}
          {props.section === 'machines' && <Devices />}
          {props.section === 'skills' && <Skills tab={tab} />}
          {props.section === 'memory' && <Memory />}
          {props.section === 'credits' && <Credits />}
          {props.section === 'audit' && <AuditRoom />}
          {plugin ? plugin.render(tab) : null}
          {missingPlugin ? (
            <div className="c42-page"><div className="c42-pwrap">
              <div className="c42-phd">
                <h2>That room is not here</h2>
                <p>
                  Something asked for the settings of a plugin this build does not
                  have installed{missingPlugin ? <> — <code>{missingPlugin}</code></> : null}.
                  Nothing is wrong with your account and nothing is missing from the
                  list on the left; there is simply no room behind that name.
                </p>
              </div>
              <button className="c42-mini" onClick={() => props.onSection('profile')}>
                Go to Profile
              </button>
            </div></div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The platform door.
 *
 * Same shape as Settings for the same reason — everything behind it is set up
 * once and changed rarely — and deliberately a separate door, because what is
 * behind it is the deployment rather than one person's use of it. Somebody who
 * cannot open it never sees that it exists.
 */
function PlatformModal(props: {
  section: PlatformSection;
  onSection: (s: PlatformSection) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  // One room. Billing & Stripe went with the payment integration: credit is
  // bought once, at rule42 cloud, and spent everywhere (ADR 0011).
  // Expanded, for the same reason as the settings dialog: a list of every
  // account on the deployment does not fit in a column.
  const [wide, setWide] = useState(true);

  const rooms: Array<[PlatformSection, string]> = [
    ['credits', 'Credits'],
    ['policies', 'Credit policy'],
    ['git-hosts', 'Git hosts'],
    ['ai-services', 'AI services'],
  ];

  return (
    <div className="c42-modal-back centred" onClick={props.onClose}>
      {/* Full screen belongs to the dialog rather than to any one room: the
          thing that is too small is the window, and a list of every account on
          the deployment is not the only place that bites. */}
      <div className={`c42-settings${wide ? ' full' : ''}`}
        role="dialog" aria-modal="true" aria-label="Platform"
        onClick={(e) => e.stopPropagation()}>
        <nav className="c42-settings-nav">
          <div className="c42-settings-title">Platform</div>
          {rooms.map(([id, label]) => (
            <button key={id} className="c42-nav" aria-current={props.section === id}
              onClick={() => props.onSection(id)}>
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="c42-settings-body">
          <DialogTools
            wide={wide} onWide={() => setWide((was) => !was)} onClose={props.onClose}
          />
          {props.section === 'credits' && <PlatformCredits />}
          {props.section === 'policies' && <PlatformPolicies />}
          {props.section === 'git-hosts' && <PlatformGitHosts />}
          {props.section === 'ai-services' && <PlatformAiServices />}
        </div>
      </div>
    </div>
  );
}

/**
 * Navigation lives in the rail's foot. A host that suppressed the rail
 * navigates its own way, which is why this is not rendered independently.
 *
 * The counters are gone. "In flight 1" beside a rail already showing the one
 * running session was the same fact in two places, and the rail is where the
 * work is — that entry has gone entirely, because clicking a session is how you
 * reach a session.
 *
 * Inbox stays, without its number. The flags in the rail say which sessions
 * want something; the Inbox is still where a decision is actually answered, and
 * an approval that could only be reached from a counter nobody kept would be an
 * approval nobody answers.
 */
/**
 * Whether the session list is showing.
 *
 * Closed by default on a narrow screen, where a 260px column beside a
 * conversation leaves neither of them usable, and open on a wide one where it
 * is the point of the layout. Remembered afterwards, because it is a preference
 * once somebody has expressed one.
 *
 * The initial guess is made once, from the width at load. It is deliberately
 * not re-guessed on resize: somebody who opened the rail and then narrowed the
 * window meant to open it.
 */
function useRailOpen(): [boolean, (open: boolean) => void] {
  const KEY = 'chat42.rail';
  const [open, setOpen] = useState(() => {
    try {
      const stored = globalThis.localStorage?.getItem(KEY);
      if (stored === 'open') return true;
      if (stored === 'closed') return false;
    } catch { /* private window. The width is a fine answer. */ }
    return (globalThis.innerWidth ?? 1024) >= 860;
  });

  const set = useCallback((next: boolean) => {
    setOpen(next);
    try {
      globalThis.localStorage?.setItem(KEY, next ? 'open' : 'closed');
    } catch { /* it still works this session. */ }
  }, []);

  return [open, set];
}

/**
 * The rail's navigation, at the top where the destinations are.
 *
 * The names are Claude Code's, because they are the names people arrive
 * already knowing: **Routines** is what we called Schedules, **Settings** is
 * the settings door — which is already connectors, skills and plugins in one
 * place, and is in the account menu at the top right — and **Artifacts** is the
 * documents this has written for you.
 *
 * Everything that is a destination rather than a daily verb goes behind
 * **More**. Inbox and Ready work are real screens and both are places you go
 * deliberately, so putting them beside New costs the two entries above them
 * their prominence and buys nothing.
 *
 * Dispatch is not here. It is a bridge between a phone and a desktop app, and
 * this already runs in a browser on both.
 */
function NavTop(props: {
  view: View; setView: (v: View) => void;
  schedules: number;
  showInbox: boolean; showSchedules: boolean; showWork: boolean;
  showArtifacts: boolean;
  /** The summary of every background task, or undefined when there are none. */
  tasks?: string | undefined;
  tasksNeedYou: number;
  tasksRunning: number;
  tasksOpen: boolean;
  onTasks: () => void;
  onNew: () => void;
  drafting: boolean;
}) {
  const [more, setMore] = useState(false);
  const extras = props.showInbox || props.showWork;
  /**
   * Every task, across every conversation, in one place.
   *
   * Present only once there is a task: an entry for a list that has never had
   * anything in it is a door to an empty room. Beside Routines, because both
   * are work running without a person watching it.
   */
  const tasksEntry = props.tasks && (
    <button className="c42-nav" aria-expanded={props.tasksOpen}
      aria-label={`Background tasks, ${props.tasks}`} onClick={props.onTasks}>
      <span>Background tasks</span>
      {props.tasksNeedYou > 0
        ? <span className="mono c42-navattn">{props.tasksNeedYou}</span>
        : props.tasksRunning > 0 && <span className="mono">{props.tasksRunning}</span>}
    </button>
  );

  return (
    <div className="c42-navtop">
      <button className="c42-nav c42-navnew" aria-current={props.drafting}
        onClick={props.onNew} title="⌘N">
        <span><span className="c42-navplus">＋</span> New</span>
      </button>

      {props.showArtifacts && (
        <button className="c42-nav" aria-current={props.view === 'artifacts'}
          onClick={() => props.setView('artifacts')}>
          <span>Artifacts</span>
        </button>
      )}
      {props.showSchedules && (
        <button className="c42-nav" aria-current={props.view === 'schedules'}
          onClick={() => props.setView('schedules')}>
          <span>Routines</span>
          {props.schedules > 0 && <span className="mono">{props.schedules}</span>}
        </button>
      )}
      {tasksEntry}

      {extras && (
        <button className="c42-nav c42-navmore" aria-expanded={more}
          onClick={() => setMore(!more)}>
          <span><span className="c42-navcaret">{more ? '⌄' : '›'}</span> More</span>
        </button>
      )}
      {more && (
        <>
          {props.showInbox && (
            <button className="c42-nav c42-navsub" aria-current={props.view === 'inbox'}
              onClick={() => props.setView('inbox')}>
              <span>Inbox</span>
            </button>
          )}
          {props.showWork && (
            <button className="c42-nav c42-navsub" aria-current={props.view === 'work'}
              onClick={() => props.setView('work')}>
              <span>Ready work</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The bottom of the rail: how the fleet is doing.
 *
 * Settings and Platform used to live here too, and moved to the account menu at
 * the top right — see AccountMenu. What stays is the connection, because it is
 * a fact about the list above it: the sessions shown are only as current as the
 * stream, and the place to say they may be stale is beside them.
 */
function NavFoot(props: { onReconnect: () => void }) {
  return (
    // Said plainly rather than hidden. A stale fleet that looks live is worse
    // than one that admits it is stale.
    <button className="c42-nav" onClick={props.onReconnect}>
      <span style={{ color: 'var(--c42-attn)' }}>Disconnected — retry</span>
    </button>
  );
}

export type Chat42Props = Omit<Chat42ProviderProps, 'children'>;

/** Everything, wired. The reference consumer of this package renders exactly this. */
export function Chat42(props: Chat42Props) {
  return (
    <Chat42Provider {...props}>
      <ReloadInPlace />
    </Chat42Provider>
  );
}

/**
 * The last resort: the whole workspace behind one boundary, which redraws it in place.
 *
 * Everything below has a boundary nearer to it, so what reaches this one is a failure in the
 * workspace's own code. Before there was any boundary, that — or anything at all — unmounted the
 * app and left a host's frame blank (2026-10-18). Now it is a calm full panel with Reload.
 *
 * **Reload is in place, not a page load.** It asks for the fleet afresh and then draws the
 * workspace again under a new key; the client, its stream and its token stay where they are. In
 * a host's frame a page load would throw away the token the host handed over and wait on the host
 * to send another, and the session the host asked for with it. The page's own reload is still
 * there for a failure above this one (`apps/web`).
 *
 * The only boundary that reports itself `blocking`, because it is the only one whose fallback
 * leaves the person unable to use the session. A host is told that and nothing smaller.
 */
function ReloadInPlace() {
  const { client, theme, hostSessionId } = useChat42();
  const [generation, setGeneration] = useState(0);
  // The fleet first, then the redraw. Drawn the moment Reload is pressed, the workspace would be
  // drawn from the same state that failed and fail again before the fresh one arrived; and with
  // nothing new for the boundary to watch, it would then stay on this panel with good data behind
  // it. A refresh that fails redraws anyway, from what there is.
  const reload = useCallback(async () => {
    await client.refresh().catch(() => undefined);
    setGeneration((n) => n + 1);
  }, [client]);
  return (
    // The session a host asked for, so a failure up here is still filed under it; inside a
    // conversation the conversation's own id takes over.
    <FailureSessionCtx.Provider value={hostSessionId}>
    <Boundary
      area="app" blocking resetKeys={[generation]}
      fallback={({ error }) => (
        // The shell's own frame and the host's theme, so the panel is in the colours the rest of
        // the frame was. Its columns are closed: there is nothing beside it.
        <div className="c42" style={{ ...themeStyle(theme), '--c42-rail-w': '0px', '--c42-ctx-w': '0px' } as React.CSSProperties}>
          <AppFailure error={error} onReload={reload} />
        </div>
      )}
    >
      <Chat42Workspace key={generation} />
    </Boundary>
    </FailureSessionCtx.Provider>
  );
}
